import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { injectIntl, IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import Text from '../../../global/text/text';
import { stringifyStaffMembers } from '../../../../infrastructure/utils/users.utils';
import { DeliveryTeamMemberRole, EventStatus, IDeliveryItemDetailsDTO, IEntityRoleDTO, IEventTrackerDetailsDTO } from '../../../../api/events.dao';
import { IRuntimeConfiguration } from '../../../../infrastructure/runtime-configuration';
import { EventLinksButtonWrapper } from '../event-links-button/event-links-button-wrapper';
import './event-tracker-list-item.scss';
import EventNameColumn from '../../../_events/_events-list/_event-list-item/_event-name-column/_event-name-column';
import { getFormattedStartDateString } from '../../../../infrastructure/utils/date.util';
import { DateTimeFormatter } from '../../../../infrastructure/implementations/date-formatter';
import DeliveryTeamButtonWrapper from './delivery-team-button/delivery-team-button-wrapper';
import { LinkTargetValue } from '../../../../constants/common.constants';

interface IProps extends IEventTrackerDetailsDTO {
    intl: IntlShape;
    searchQuery?: string;
    viewPlanningNotesAccess: boolean;
    canRenderAddMembersButton: boolean;
    isFirstInTimeGroup: boolean;
    isSelected: boolean;
    composedId: string;
    withDateColumn?: boolean;
    withTimeColumn?: boolean;
    withTotalPresenterCombinationsByDate?: boolean;
    onPlanningNotesClick: (accountId: number, eventId: number, composedId: string, intl: IntlShape) => void;
    setAssignedDeliveryTeam: (
        deliveryTeamMembers: IDeliveryItemDetailsDTO[],
        eventId: number,
        accountId: number
    ) => Promise<void>;
    fetchDeliveryTeamItems: (query: string) => Promise<IDeliveryItemDetailsDTO[]>;
    removeDeliveryItem: (
        intl: IntlShape,
        deliveryMemberUserName: string,
        deliveryMemberUserId: string,
        eventId: number,
        accountId: number
    ) => Promise<void>;
    setDeliveryMemberRole: (
        intl: IntlShape,
        deliveryMemberUserName: string,
        deliveryMemberUserId: string,
        eventId: number,
        accountId: number,
        role: string
    ) => Promise<void>;
    startEditEvent: () => void;
    showColumn: (id) => boolean;
    fetchEventsList: () => Promise<void>;
    wideTdName?: string;
    trackCancelDeliveryTeamButtonMixPanel?: () => void;
    deliveryMemberRoles: IEntityRoleDTO[];
    className?: string;
}

const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);

const EventTrackerListItem = injectIntl((props: IProps) => {
    const {
        name,
        type,
        eventId,
        accountName,
        accountId,
        oeNumber,
        searchQuery,
        coordinators,
        deliveryTeamMembers,
        planningNotes,
        mergedDateTime,
        startDateTime,
        endDateTime,
        withTimeColumn,
        withDateColumn,
        withTotalPresenterCombinationsByDate,
        intl,
        viewPlanningNotesAccess,
        canRenderAddMembersButton,
        isFirstInTimeGroup,
        composedId,
        isSelected,
        onPlanningNotesClick,
        fetchDeliveryTeamItems,
        setAssignedDeliveryTeam,
        removeDeliveryItem,
        setDeliveryMemberRole,
        subdomain,
        region,
        startEditEvent,
        businessLine,
        showColumn,
        links,
        fetchEventsList,
        wideTdName,
        trackCancelDeliveryTeamButtonMixPanel,
        totalPresenters,
        totalPresenterCombinationsByDate,
        deliveryMemberRoles,
        status,
        className
    } = props;

    const [deliveryTeamItems, setDeliveryTeamItems] = useState<IDeliveryItemDetailsDTO[] | undefined>();

    const getEventUrl = (): string => {
        const runtimeConfiguration = IoC.Container.get(IRuntimeConfiguration);
        const domain = runtimeConfiguration.getAppDomain();

        return `https://connect.${domain}.com/account/${accountId}/events/${eventId}`;
    };

    const sortedDeliveryTeamMembers = useMemo(() => deliveryTeamMembers.slice()
        .sort((a, b) => a.lastName.localeCompare(b.lastName))
        .sort((a, b) => {
            if (a.deliveryTeamMemberRole === DeliveryTeamMemberRole.PROJECT_MANAGER &&
                b.deliveryTeamMemberRole !== DeliveryTeamMemberRole.PROJECT_MANAGER) {
                return -1;
            } else if (a.deliveryTeamMemberRole === DeliveryTeamMemberRole.CO_PROJECT_MANAGER &&
                b.deliveryTeamMemberRole === DeliveryTeamMemberRole.PROJECT_MANAGER) {
                return 1;
            } else if (a.deliveryTeamMemberRole === DeliveryTeamMemberRole.CO_PROJECT_MANAGER &&
                b.deliveryTeamMemberRole !== DeliveryTeamMemberRole.CO_PROJECT_MANAGER) {
                return -1;
            }
        }), [deliveryTeamMembers]);

    const displayedTime = useMemo(() => {
        const startTime = startDateTime && dateTimeFormatter.dateToFormattedString(
            startDateTime,
            dateTimeFormatter.getLocalTimeFormat()
        );
        const endTime = endDateTime && dateTimeFormatter.dateToFormattedString(
            endDateTime,
            dateTimeFormatter.getLocalTimeFormat()
        );
        return `${startTime || ''}${endTime ? ' - ' + endTime : ''}`;
    }, [startDateTime, endDateTime]);

    const handleClick = () => {
        window.open(getEventUrl(), LinkTargetValue.BLANK, 'noopener, noreferrer');
    };

    const handlePlanningNotesClick = event => {
        event.stopPropagation();
        onPlanningNotesClick(accountId, eventId, composedId, intl);
    };

    const handleSearchDeliveryTeamItems = async (query: string): Promise<void> => {
        const items = await fetchDeliveryTeamItems(query);
        setDeliveryTeamItems(items);
    };

    const cellEffectClasses = classnames({
        'active': isSelected,
        'hoverable': !isSelected,
        'event-tracker-item--first': isFirstInTimeGroup,
    });

    const getColSpan = (id: string): number => {
        return id === wideTdName ? 3 : 1;
    };

    const nameColSpan = !wideTdName ? 3 : 1;

    const eventCancelledClass = classnames({ 'event-tracker-item__cancelled': status === EventStatus.CANCELLED });

    return (
        <tr className={classnames('event-tracker-item', className, eventCancelledClass)}>
            { withDateColumn && showColumn('dateTime') && <td onClick={handleClick} className={cellEffectClasses} colSpan={getColSpan('dateTime')}>
                <span className='event-name-column__day stringified-day'>
                    {getFormattedStartDateString(mergedDateTime, endDateTime)}
                </span>
            </td>
            }
            <td className={cellEffectClasses} colSpan={nameColSpan + 1}>
                <EventNameColumn
                    className='event-tracker-item__event-name'
                    name={name}
                    onEditEvent={startEditEvent}
                    searchQuery={searchQuery}
                    handleClick={handleClick}
                />
            </td>
            { withTimeColumn && showColumn('displayedTime') &&
                <Text onClick={handleClick} className={cellEffectClasses} tag='td' colSpan={getColSpan('displayedTime')} message={displayedTime} withEllipsis />
            }
            {showColumn('account') &&
                <Text onClick={handleClick} className={`${cellEffectClasses} column-with-filters`} tag='td' colSpan={getColSpan('account')} message={accountName} withEllipsis />
            }
            {showColumn('oeNumber') &&
                <Text onClick={handleClick} className={cellEffectClasses} tag='td' colSpan={getColSpan('oeNumber')} message={oeNumber} withEllipsis />
            }
            {showColumn('region') &&
                <Text onClick={handleClick} className={`${cellEffectClasses} column-with-filters`} tag='td' colSpan={getColSpan('region')} message={region} withEllipsis />
            }
            {showColumn('businessLine') &&
                <Text onClick={handleClick} className={`${cellEffectClasses} column-with-filters`} tag='td' colSpan={getColSpan('businessLine')} message={businessLine} withEllipsis />
            }
            {showColumn('type') &&
                <Text
                    onClick={handleClick}
                    className={`${cellEffectClasses} column-with-filters`}
                    tag='td'
                    colSpan={getColSpan('type')}
                    message={intl.formatMessage({ id: `oe.eventType.${type}` })}
                    withEllipsis
                />
            }
            {showColumn('totalPresenters') &&
                <td onClick={handleClick} className={`${cellEffectClasses} text-center`} colSpan={getColSpan('totalPresenters')}>
                    {!!totalPresenters && totalPresenters}
                </td>
            }
             {withTotalPresenterCombinationsByDate && showColumn('totalPresenterCombinationsByDate') &&
                <td onClick={handleClick} className={`${cellEffectClasses} text-center`} colSpan={getColSpan('totalPresenterCombinationsByDate')}>
                    {!!totalPresenterCombinationsByDate && totalPresenterCombinationsByDate}
                </td>
            }
            {showColumn('coordinators') &&
                <td onClick={handleClick} className={cellEffectClasses} colSpan={getColSpan('coordinators')}>
                    <div title={stringifyStaffMembers(coordinators).join('\n')}>
                        {stringifyStaffMembers(coordinators).map((person, index) => {
                            if (index <= 1) return <Text key={person + index} message={person} withEllipsis withoutTitle />;
                        })}
                    </div>
                </td>
            }
            {showColumn('deliveryTeam') &&
                <td
                    className={classnames(cellEffectClasses, 'organizations-attendees-list-item')}
                    colSpan={getColSpan('deliveryTeam')}
                >
                    <DeliveryTeamButtonWrapper
                        canRenderAddMembersButton={canRenderAddMembersButton}
                        deliveryTeamItems={deliveryTeamItems}
                        handleSearchDeliveryTeamItems={handleSearchDeliveryTeamItems}
                        setAssignedDeliveryTeam={setAssignedDeliveryTeam}
                        intl={intl}
                        accountId={accountId}
                        eventId={eventId}
                        sortedDeliveryTeamMembers={sortedDeliveryTeamMembers}
                        removeDeliveryItem={removeDeliveryItem}
                        setDeliveryMemberRole={setDeliveryMemberRole}
                        trackCancelDeliveryTeamButtonMixPanel={trackCancelDeliveryTeamButtonMixPanel}
                        deliveryMemberRoles={deliveryMemberRoles}
                    />
                </td>
            }
            {showColumn('links') &&
                <td className={classnames(cellEffectClasses, 'organizations-attendees-list-item')} colSpan={getColSpan('links')}>
                    {<EventLinksButtonWrapper
                        subdomain={subdomain}
                        accountId={accountId}
                        eventId={eventId}
                        links={links}
                        fetchEventsList={fetchEventsList}
                    />
                    }
                </td>
            }
            { viewPlanningNotesAccess && showColumn('notes') && (
                <td onClick={handleClick} className={cellEffectClasses} colSpan={getColSpan('notes')}>
                    <Text
                        className='planning-notes-column'
                        onClick={handlePlanningNotesClick}
                        message={planningNotes}
                        withEllipsis
                    />
                </td>
            )}
        </tr>
    );
});

export { EventTrackerListItem };
